import React, { useMemo, useState } from 'react';

import { Combobox } from 'components/Combobox';
import { useToastHelper } from 'hooks/useToastHelper';
import useQuery from 'hooks/useQuery';
import services from 'services/services';
import { DSCOUT_MISSION_DELIMITER } from 'utils/constants';

/**
 * Searchable dropdown for selecting a Dscout mission.
 * Missions without a splash URL are shown as disabled with a note.
 *
 * @param {Function} onChange Callback function triggered when the selected option changes.
 * @param {string|null} selectedMissionId Composite ID of already-linked mission ({type}:{id}), or null.
 */
export function DscoutMissionSelect({ onChange, selectedMissionId }) {
    const { addErrorToast } = useToastHelper();
    const [value, setValue] = useState(selectedMissionId);

    const { data: missions, isLoading } = useQuery({
        queryFn: () => {
            return selectedMissionId ? services.getDscoutMission(selectedMissionId) : services.getDscoutMissions();
        },
        onSuccess: response => {
            if (selectedMissionId) {
                const [type] = selectedMissionId.split(DSCOUT_MISSION_DELIMITER);
                onChange({ ...response.mission, type });
            }
        },
        onError: (_, errorText) => {
            addErrorToast(errorText);
        }
    });

    const { items, missionsMap } = useMemo(() => {
        if (!missions) return { items: [], missionsMap: {} };

        if (selectedMissionId) {
            const [type] = selectedMissionId.split(DSCOUT_MISSION_DELIMITER);
            const mission = missions.mission;
            const projectName = mission.project ? mission.project.name : 'Unknown project';
            return {
                items: [
                    {
                        value: selectedMissionId,
                        label: `[${projectName}] ${mission.name} (${type})`
                    }
                ],
                missionsMap: { [selectedMissionId]: { ...mission, type } }
            };
        }

        const items = [];
        const map = {};

        Object.keys(missions).forEach(missionType => {
            missions[missionType].forEach(mission => {
                const projectName = mission.project ? mission.project.name : 'Unknown project';
                const compositeValue = `${missionType}${DSCOUT_MISSION_DELIMITER}${mission.id}`;
                const hasSplashUrl = !!mission.splashUrl;

                items.push({
                    value: compositeValue,
                    label: `[${projectName}] ${mission.name} (${missionType})`,
                    disabled: !hasSplashUrl,
                    description: !hasSplashUrl ? 'Enable splash page in dscout to use this mission' : undefined
                });

                map[compositeValue] = { ...mission, type: missionType };
            });
        });

        return { items, missionsMap: map };
    }, [missions, selectedMissionId]);

    const handleChange = compositeValue => {
        setValue(compositeValue);
        const mission = missionsMap[compositeValue];
        onChange(mission || null);
    };

    if (isLoading) {
        return <div className="px-2 py-1">Loading missions...</div>;
    }

    return <Combobox items={items} value={value} onChange={handleChange} ariaLabel="Select a dscout mission" />;
}

export default DscoutMissionSelect;
